import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

const PEOPLE_IMPORT_API = '/api/v1/persons/import';

export const PEOPLE_IMPORT_FIELDS = [
  { value: 'first_name', label: 'First Name', required: true },
  { value: 'last_name', label: 'Last Name', required: true },
  { value: 'email', label: 'Email', required: false },
  { value: 'phone', label: 'Phone', required: false },
  { value: 'job_title', label: 'Job Title', required: false },
  { value: 'cert_body', label: 'Cert Body', required: false },
  { value: 'cert_level', label: 'Cert Level', required: false },
  { value: 'cert_expiry', label: 'Cert Expiry', required: false },
] as const;

export type PersonImportField = typeof PEOPLE_IMPORT_FIELDS[number]['value'];

export interface PersonColumnMapping {
  csvColumn: string;
  personField: PersonImportField | null;
}

export interface RowResult {
  row: number;
  status: 'ready' | 'duplicate' | 'invalid' | 'needs_review';
  reason: string;
  data: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    job_title: string;
  };
  cert: null | {
    status: 'ready' | 'unresolved';
    cert_type: string | null;
    cert_body: string;
    cert_level: string;
    expiry: string | null;
    reason: string;
  };
}

export interface ImportSummary {
  total: number;
  ready: number;
  duplicate: number;
  invalid: number;
  needs_review: number;
  cert_ready: number;
}

export interface ImportPreviewResponse {
  summary: ImportSummary;
  rows: RowResult[];
}

export interface ImportCommitRowResult extends RowResult {
  person_id?: number;
  tech_profile_id?: number;
  certification_id?: number;
}

export interface ImportCommitResponse {
  summary: ImportSummary;
  rows: ImportCommitRowResult[];
  import_id: number;
}

const PERSON_COLUMN_ALIASES: Record<string, PersonImportField> = {
  firstname: 'first_name',
  'first_name': 'first_name',
  'first name': 'first_name',
  'given name': 'first_name',
  givenname: 'first_name',
  forename: 'first_name',
  surname: 'last_name',
  lastname: 'last_name',
  'last_name': 'last_name',
  'last name': 'last_name',
  'family name': 'last_name',
  email: 'email',
  'e-mail': 'email',
  'e mail': 'email',
  phone: 'phone',
  mobile: 'phone',
  cell: 'phone',
  title: 'job_title',
  role: 'job_title',
  'job title': 'job_title',
  jobtitle: 'job_title',
  cert: 'cert_body',
  certification: 'cert_body',
  'cert body': 'cert_body',
  certbody: 'cert_body',
  level: 'cert_level',
  'cert level': 'cert_level',
  certlevel: 'cert_level',
  expiry: 'cert_expiry',
  'expiry date': 'cert_expiry',
  'cert expiry': 'cert_expiry',
  certexpiry: 'cert_expiry',
};

const normalizeHeader = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();

export function autoDetectPersonColumnMapping(headers: string[]): PersonColumnMapping[] {
  return headers.map((csvColumn) => {
    const personField = PERSON_COLUMN_ALIASES[normalizeHeader(csvColumn)] ?? null;
    return { csvColumn, personField };
  });
}

function buildPersonImportMapping(columnMapping: PersonColumnMapping[]): Record<string, string> {
  return columnMapping.reduce<Record<string, string>>((acc, mapping) => {
    if (mapping.personField) {
      acc[mapping.personField] = mapping.csvColumn;
    }
    return acc;
  }, {});
}

function buildPersonImportFormData(
  file: File,
  columnMapping: PersonColumnMapping[]
): FormData {
  const formData = new FormData();
  formData.append('file', file);

  const mapping = buildPersonImportMapping(columnMapping);
  if (Object.keys(mapping).length > 0) {
    formData.append('mapping', JSON.stringify(mapping));
  }

  return formData;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function usePreviewPersonImport() {
  return useMutation({
    mutationFn: async ({
      file,
      columnMapping,
    }: {
      file: File;
      columnMapping: PersonColumnMapping[];
    }): Promise<ImportPreviewResponse> => {
      const response = await apiClient.post<ImportPreviewResponse>(
        `${PEOPLE_IMPORT_API}/preview/`,
        buildPersonImportFormData(file, columnMapping),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
  });
}

export function useCommitPersonImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      columnMapping,
    }: {
      file: File;
      columnMapping: PersonColumnMapping[];
    }): Promise<ImportCommitResponse> => {
      const response = await apiClient.post<ImportCommitResponse>(
        `${PEOPLE_IMPORT_API}/commit/`,
        buildPersonImportFormData(file, columnMapping),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
    },
  });
}

export function useDownloadPeopleTemplate() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await apiClient.get(`${PEOPLE_IMPORT_API}/template/`, {
        responseType: 'blob',
      });

      downloadBlob(new Blob([response.data], { type: 'text/csv' }), 'people_import_template.csv');
    },
  });
}
